import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { Link, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
};

const NewsletterViewer = ({ userData }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filteredPostId, setFilteredPostId] = useState(null);
  const [filteredResponseId, setFilteredResponseId] = useState(null);
  const [myPosts, setMyPosts] = useState([]);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [showCommentReplyForm, setShowCommentReplyForm] = useState(null);
  const [commentReplyContent, setCommentReplyContent] = useState('');
  const [submittingCommentReply, setSubmittingCommentReply] = useState(false);
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [showEditForm, setShowEditForm] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editSettings, setEditSettings] = useState({
    allowMainResponse: true,
    allowCommentResponse: true,
    notifyMeOnResponses: true,
    allowAssigneesViewResponses: true
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [showRecipients, setShowRecipients] = useState(null);
  const [recipientsList, setRecipientsList] = useState({});
  const [loadingRecipients, setLoadingRecipients] = useState({});
  const [activeTab, setActiveTab] = useState('toMe'); // 'toMe', 'myPosts', 'repliedTo'
  const [repliedToPosts, setRepliedToPosts] = useState([]);
  const [loadingRepliedTo, setLoadingRepliedTo] = useState(false);
  const [showMyResponse, setShowMyResponse] = useState({});
  const location = useLocation();

  useEffect(() => {
    loadNewsletterPosts();
    loadMyPosts();
  }, [userData]);

  // Inject Quill styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = quillStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Load replied to posts when tab is active
  useEffect(() => {
    if (activeTab === 'repliedTo') {
      loadRepliedToPosts();
    } else if (activeTab === 'myPosts') {
      loadMyPosts();
    }
  }, [activeTab, userData]);

  // Check for URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const command = urlParams.get('command');
    const id = urlParams.get('id');
    const responseId = urlParams.get('responseId');
    
    console.log('URL params - command:', command, 'id:', id, 'responseId:', responseId);
    
    if (command === 'filterToId' && id) {
      console.log('Setting filteredPostId to:', id);
      setFilteredPostId(id);
      setActiveTab('toMe');
    } else if (command === 'filterToMyId' && id) {
      console.log('Setting filteredPostId to:', id, 'and switching to My Posts tab');
      setFilteredPostId(id);
      setFilteredResponseId(responseId);
      setActiveTab('myPosts');
    }
  }, [location.search]);

  const loadNewsletterPosts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Try both reference_id values since we changed how newsletter posts are stored
      const response = await axios.get('/api/comments?reference_id=self&table=newsletter', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('API Response:', response.data);
      console.log('All comments from API:', response.data.comments);
      console.log('Current userData.account_id:', userData?.account_id);
      
      // Filter posts where current user is in the assignees array
      const userPosts = response.data.comments.filter(post => {
        console.log('Checking post:', post.id, 'assignees:', post.assignees, 'type:', typeof post.assignees);
        if (!post.assignees) {
          console.log('Post has no assignees, skipping');
          return false;
        }
        let assignees = post.assignees;
        if (typeof assignees === 'string') {
          try {
            assignees = JSON.parse(assignees);
            console.log('Parsed assignees from string:', assignees);
          } catch {
            console.log('Failed to parse assignees string');
            assignees = [];
          }
        }
        const isIncluded = Array.isArray(assignees) && assignees.includes(userData?.account_id);
        console.log('Is user included in assignees?', isIncluded);
        return isIncluded;
      });
      
      console.log('Filtered userPosts:', userPosts);
      // Sort posts by createddate in descending order (latest first)
      const sortedPosts = userPosts.sort((a, b) => new Date(b.createddate) - new Date(a.createddate));
      setPosts(sortedPosts);
      
      console.log('Loaded posts:', userPosts.map(p => ({ id: p.id, title: p.content?.substring(0, 50) })));
      console.log('filteredPostId:', filteredPostId);
      
      // If we have a filtered post ID, scroll to it when posts are loaded
      if (filteredPostId && userPosts.some(post => post.id === filteredPostId)) {
        setTimeout(async () => {
          const element = document.getElementById(`post-${filteredPostId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // If we have a responseId, load comments and scroll to the specific response
            if (filteredResponseId) {
              await loadCommentsForPost(filteredPostId);
              setShowComments(prev => ({ ...prev, [filteredPostId]: true }));
              
              // Scroll to the specific response after comments are loaded
              setTimeout(() => {
                const responseElement = document.getElementById(`response-${filteredResponseId}`);
                if (responseElement) {
                  responseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  responseElement.style.border = '3px solid #2563eb';
                  responseElement.style.borderRadius = '8px';
                  // Keep the border highlight persistent
                }
              }, 1000);
            }
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error loading newsletter posts:', error);
      setError('Failed to load newsletter posts');
    } finally {
      setLoading(false);
    }
  };

  const loadMyPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/comments?reference_id=self&table=newsletter', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter posts created by the current user
      const userPosts = response.data.comments.filter(post => 
        post.account === userData?.account_id
      );
      
      // Sort posts by createddate in descending order (latest first)
      const sortedPosts = userPosts.sort((a, b) => new Date(b.createddate) - new Date(a.createddate));
      setMyPosts(sortedPosts);
      
      // If we have a filtered post ID and we're on the My Posts tab, scroll to it when posts are loaded
      if (filteredPostId && activeTab === 'myPosts' && userPosts.some(post => post.id === filteredPostId)) {
        setTimeout(async () => {
          const element = document.getElementById(`post-${filteredPostId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // If we have a responseId, load comments and scroll to the specific response
            if (filteredResponseId) {
              await loadCommentsForPost(filteredPostId);
              setShowComments(prev => ({ ...prev, [filteredPostId]: true }));
              
              // Scroll to the specific response after comments are loaded
              setTimeout(() => {
                const responseElement = document.getElementById(`response-${filteredResponseId}`);
                if (responseElement) {
                  responseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  responseElement.style.border = '3px solid #2563eb';
                  responseElement.style.borderRadius = '8px';
                  // Keep the border highlight persistent
                }
              }, 1000);
            }
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error loading my posts:', error);
    }
  };

  // Helper function to check if user can see comments based on settings
  const canUserSeeComments = (post, comment) => {
    // Poster can always see all comments
    if (post.account === userData?.account_id) {
      return true;
    }
    
    // Check if assignees can view responses
    if (!post.allowassigneesviewresponses) {
      // If assignees can't view responses, user can only see their own comments
      return comment.account === userData?.account_id;
    }
    
    // If assignees can view responses, check if user is in assignees
    let assignees = post.assignees;
    if (typeof assignees === 'string') {
      try {
        assignees = JSON.parse(assignees);
      } catch {
        assignees = [];
      }
    }
    return Array.isArray(assignees) && assignees.includes(userData?.account_id);
  };

  // Helper function to check if user can reply to main post
  const canUserReplyToMainPost = (post) => {
    return post.allowmainresponse === true;
  };

  const canUserReplyToComment = (post) => {
    return post.allowcommentresponse === true;
  };

  // Helper function to determine if "View Comments" button should be visible
  const canUserViewComments = (post) => {
    // If user is the creator of the post, they can always see comments
    if (post.account === userData?.account_id) {
      return true;
    }
    
    // If allowassigneesviewresponses is true, assignees can see comments
    if (post.allowassigneesviewresponses === true) {
      return post.assignees && (
        Array.isArray(post.assignees) 
          ? post.assignees.includes(userData?.account_id)
          : JSON.parse(post.assignees).includes(userData?.account_id)
      );
    }
    
    // If allowassigneesviewresponses is false, don't show "View Comments" button
    return false;
  };

  // Helper function to check if user has a response to this post
  const hasUserResponse = (post) => {
    return comments[post.id] && comments[post.id].some(comment => 
      comment.account === userData?.account_id
    );
  };

  // Helper function to check if user should see "My Response" button
  const shouldShowMyResponseButton = (post) => {
    // Only show if user is not the creator and allowassigneesviewresponses is false
    return post.account !== userData?.account_id && post.allowassigneesviewresponses === false;
  };

  // Helper function to get user's own responses to a post
  const getUserResponses = (post) => {
    if (!comments[post.id]) return [];
    return comments[post.id].filter(comment => 
      comment.account === userData?.account_id
    );
  };

  const renderCommentWithReplies = (comment, post, depth = 0) => {
    // Skip comments that the user can't see
    if (!canUserSeeComments(post, comment)) {
      return null;
    }
    return (
      <div key={comment.id} id={`response-${comment.id}`} style={styles.commentCard}>
        <div style={styles.commentContent}>
          <div 
            dangerouslySetInnerHTML={{ __html: comment.content }}
            style={styles.commentContentHtml}
          />
        </div>
        <div style={styles.commentHeader}>
          <div style={styles.commentAuthor}>
            {comment.first_name && comment.last_name 
              ? `${comment.first_name} ${comment.last_name}`
              : 'Unknown User'
            }
          </div>
          <div style={styles.commentDate}>
            {formatDate(comment.createddate)}
          </div>
        </div>
        {canUserReplyToComment(post) && (
          <div style={styles.commentActions}>
            <button
              onClick={() => handleCommentReplyClick(comment.id)}
              style={styles.replyButton}
              className="subtle-button"
            >
              💬 Reply
            </button>
          </div>
        )}
        {showCommentReplyForm === comment.id && (
          <div style={styles.replyForm}>
            <div style={styles.quillWrapper}>
              <ReactQuill
                value={commentReplyContent}
                onChange={setCommentReplyContent}
                modules={quillModules}
                placeholder="Write your reply..."
                style={styles.replyQuill}
              />
            </div>
            <div style={styles.replyFormActions}>
              <button
                onClick={() => handleSubmitCommentReply(comment.id, post.id)}
                disabled={submittingCommentReply || !commentReplyContent.trim()}
                style={{
                  ...styles.submitReplyButton,
                  ...(submittingCommentReply || !commentReplyContent.trim() ? styles.disabledButton : {})
                }}
              >
                {submittingCommentReply ? 'Submitting...' : 'Submit Reply'}
              </button>
              <button
                onClick={handleCancelCommentReply}
                style={styles.cancelReplyButton}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Render nested replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div style={{ ...styles.nestedReplies, marginLeft: `${depth * 20}px` }}>
            {comment.replies.map(reply => renderCommentWithReplies(reply, post, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Helper function to get settings status text
  const getSettingsStatusText = (post) => {
    const statuses = [];
    
    if (!post.allowmainresponse) {
      statuses.push('Replies disabled');
    }
    
    if (!post.allowassigneesviewresponses) {
      statuses.push('Private responses');
    }
    
    return statuses.length > 0 ? statuses.join(', ') : null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilter = () => {
    setFilteredPostId(null);
    setFilteredResponseId(null);
    // Remove URL parameters
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('command');
    urlParams.delete('id');
    urlParams.delete('responseId');
    window.history.replaceState({}, '', `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`);
  };

  const handleTabSwitch = (newTab) => {
    // Clear filters when switching tabs
    if (filteredPostId || filteredResponseId) {
      clearFilter();
    }
    setActiveTab(newTab);
  };

  const handleReplyClick = (postId) => {
    // Close any open edit forms when starting a reply
    if (showEditForm === postId) {
      setShowEditForm(null);
      setEditContent('');
    }
    
    setShowReplyForm(postId);
    setReplyContent('');
  };

  const handleCancelReply = () => {
    setShowReplyForm(null);
    setReplyContent('');
  };

  const handleCommentReplyClick = (commentId) => {
    // Close any open edit forms when starting a comment reply
    if (showEditForm === commentId) {
      setShowEditForm(null);
      setEditContent('');
    }
    
    setShowCommentReplyForm(commentId);
    setCommentReplyContent('');
  };

  const handleCancelCommentReply = () => {
    setShowCommentReplyForm(null);
    setCommentReplyContent('');
  };

  const handleSubmitReply = async (postId) => {
    if (!replyContent.trim()) return;
    
    setSubmittingReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/comments', {
        content: replyContent,
        type: 'Response',
        reference_id: postId,
        parent: postId, // Set parent to the main post ID
        table: 'newsletter',
        relevantRole: 'Responder',
        account: userData?.account_id,
        // Set all settings to false for Response type comments
        settings: {
          allowMainResponse: false,
          allowCommentResponse: false,
          notifyMeOnResponses: false,
          allowAssigneesViewResponses: false
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReplyContent('');
      setShowReplyForm(null);
      // Reload comments for this post to show the new reply
      if (showComments[postId]) {
        loadCommentsForPost(postId);
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const organizeCommentsHierarchically = (allComments) => {
    const commentMap = new Map();
    const rootComments = [];
    
    // First pass: create a map of all comments by ID
    allComments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });
    
    // Second pass: organize into hierarchy
    allComments.forEach(comment => {
      if (comment.parent && comment.parent !== comment.reference_id) {
        // This is a reply to another comment
        const parentComment = commentMap.get(comment.parent);
        if (parentComment) {
          parentComment.replies.push(commentMap.get(comment.id));
        }
      } else {
        // This is a top-level comment (reply to main post)
        rootComments.push(commentMap.get(comment.id));
      }
    });
    
    return rootComments;
  };

  const handleSubmitCommentReply = async (commentId, postId) => {
    if (!commentReplyContent.trim()) return;
    
    setSubmittingCommentReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/comments', {
        content: commentReplyContent,
        type: 'Response',
        reference_id: postId, // Reference to the main post
        parent: commentId, // Parent is the comment being replied to
        table: 'newsletter',
        relevantRole: 'Responder',
        account: userData?.account_id,
        // Set all settings to false for Response type comments
        settings: {
          allowMainResponse: false,
          allowCommentResponse: false,
          notifyMeOnResponses: false,
          allowAssigneesViewResponses: false
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCommentReplyContent('');
      setShowCommentReplyForm(null);
      // Reload comments for this post to show the new reply
      if (showComments[postId]) {
        loadCommentsForPost(postId);
      }
    } catch (error) {
      console.error('Error submitting comment reply:', error);
      alert('Failed to submit comment reply. Please try again.');
    } finally {
      setSubmittingCommentReply(false);
    }
  };

  const loadCommentsForPost = async (postId) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/comments?reference_id=${postId}&table=newsletter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Organize comments hierarchically
      const allComments = response.data.comments;
      const organizedComments = organizeCommentsHierarchically(allComments);
      
      setComments(prev => ({ ...prev, [postId]: organizedComments }));
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleViewComments = (postId) => {
    // Close any open edit forms when viewing comments
    if (showEditForm === postId) {
      setShowEditForm(null);
      setEditContent('');
    }
    
    if (!showComments[postId]) {
      setShowComments(prev => ({ ...prev, [postId]: true }));
      loadCommentsForPost(postId);
    } else {
      setShowComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleViewMyResponse = (postId) => {
    // Close any open edit forms when viewing my response
    if (showEditForm === postId) {
      setShowEditForm(null);
      setEditContent('');
    }
    
    if (!showMyResponse[postId]) {
      setShowMyResponse(prev => ({ ...prev, [postId]: true }));
      // Always load comments when viewing my response
      loadCommentsForPost(postId);
    } else {
      setShowMyResponse(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleEditClick = (postId, currentContent, currentTitle, currentSettings) => {
    // Close any open reply forms when starting to edit
    if (showReplyForm === postId) {
      setShowReplyForm(null);
      setReplyContent('');
    }
    
    setShowEditForm(postId);
    // ReactQuill expects HTML content, so we pass it directly
    setEditContent(currentContent || '');
    setEditTitle(currentTitle || '');
    setEditSettings(currentSettings || {
      allowMainResponse: true,
      allowCommentResponse: true,
      notifyMeOnResponses: true,
      allowAssigneesViewResponses: true
    });
  };

  const handleCancelEdit = () => {
    setShowEditForm(null);
    setEditContent('');
    setEditTitle('');
    setEditSettings({
      allowMainResponse: true,
      allowCommentResponse: true,
      notifyMeOnResponses: true,
      allowAssigneesViewResponses: true
    });
  };

  const handleSubmitEdit = async (postId) => {
    if (!editContent.trim() || !editTitle.trim()) return;
    
    setSubmittingEdit(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/comments/${postId}`, {
        title: editTitle,
        content: editContent,
        settings: editSettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditContent('');
      setEditTitle('');
      setEditSettings({
        allowMainResponse: true,
        allowCommentResponse: true,
        notifyMeOnResponses: true,
        allowAssigneesViewResponses: true
      });
      setShowEditForm(null);
      // Reload posts to show the updated content
      loadNewsletterPosts();
      loadMyPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post. Please try again.');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const loadRecipients = async (postId, assignees) => {
    if (!assignees || assignees.length === 0) return;
    
    setLoadingRecipients(prev => ({ ...prev, [postId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/user-management/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter accounts that are in the assignees array
      const recipients = response.data.accounts.filter(account => 
        assignees.includes(account.account_id)
      );
      
      setRecipientsList(prev => ({ ...prev, [postId]: recipients }));
    } catch (error) {
      console.error('Error loading recipients:', error);
    } finally {
      setLoadingRecipients(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleRecipientsClick = (postId, assignees) => {
    if (showRecipients === postId) {
      setShowRecipients(null);
    } else {
      setShowRecipients(postId);
      // Load recipients if not already loaded
      if (!recipientsList[postId]) {
        loadRecipients(postId, assignees);
      }
    }
  };

  const loadRepliedToPosts = async () => {
    setLoadingRepliedTo(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get all newsletter posts
      const postsResponse = await axios.get('/api/comments?reference_id=self&table=newsletter', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('All newsletter posts:', postsResponse.data.comments);
      
      // Filter posts where the user has made responses
      const postsUserRepliedTo = [];
      
      for (const post of postsResponse.data.comments) {
        // Load comments for this post to check if user has responded
        const commentsResponse = await axios.get(`/api/comments?reference_id=${post.id}&table=newsletter`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const userResponses = commentsResponse.data.comments.filter(comment => 
          comment.account === userData?.account_id
        );
        
        console.log(`Post ${post.id}: User responses:`, userResponses);
        
        if (userResponses.length > 0) {
          postsUserRepliedTo.push(post);
        }
      }
      
      console.log('Posts user replied to:', postsUserRepliedTo);
      // Sort posts by createddate in descending order (latest first)
      const sortedPosts = postsUserRepliedTo.sort((a, b) => new Date(b.createddate) - new Date(a.createddate));
      setRepliedToPosts(sortedPosts);
    } catch (error) {
      console.error('Error loading replied to posts:', error);
    } finally {
      setLoadingRepliedTo(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2 style={styles.errorTitle}>Error</h2>
          <p style={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Newsletter Posts</h1>
        <p style={styles.subtitle}>
          {activeTab === 'toMe' && "Posts shared with you by other users"}
          {activeTab === 'myPosts' && "Posts you have created"}
          {activeTab === 'repliedTo' && "Posts you have commented on"}
        </p>
      </div>

      <div style={styles.tabContainer}>
        <div style={styles.tabSection}>
          <button
            onClick={() => handleTabSwitch('toMe')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'toMe' ? styles.activeTabButton : {})
            }}
            className={`subtle-button tab-button ${activeTab === 'toMe' ? 'active' : ''}`}
          >
            📬 To Me
          </button>
          {(() => {
            const defaultAccessCard = userData?.accesscards?.find(
              ac => ac.accesscard_id === userData.default_accesscard_id
            );
            return defaultAccessCard?.role !== 'Student';
          })() && (
            <button
              onClick={() => handleTabSwitch('myPosts')}
              style={{
                ...styles.tabButton,
                ...(activeTab === 'myPosts' ? styles.activeTabButton : {})
              }}
              className={`subtle-button tab-button ${activeTab === 'myPosts' ? 'active' : ''}`}
            >
              📝 My Posts
            </button>
          )}
          <button
            onClick={() => handleTabSwitch('repliedTo')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'repliedTo' ? styles.activeTabButton : {})
            }}
            className={`subtle-button tab-button ${activeTab === 'repliedTo' ? 'active' : ''}`}
          >
            💬 Posts I've Replied to
          </button>
        </div>
        
        <div style={styles.createButtonSection}>
          {(() => {
            const defaultAccessCard = userData?.accesscards?.find(
              ac => ac.accesscard_id === userData.default_accesscard_id
            );
            return defaultAccessCard?.role !== 'Student';
          })() && (
            <Link to="/newsletter" style={styles.createButton}>
              ✏️ Create New Post
            </Link>
          )}
        </div>
      </div>

      {/* My Posts Section */}
      {activeTab === 'myPosts' && (
        <div style={styles.myPostsContainer}>
          <h2 style={styles.myPostsTitle}>My Newsletter Posts</h2>
          {filteredPostId && (
            <div style={styles.filterBanner}>
              <span>Showing filtered post</span>
              <button onClick={clearFilter} style={styles.clearFilterButton}>
                Clear Filter
              </button>
            </div>
          )}
          {myPosts.length === 0 ? (
            <div style={styles.emptyMyPosts}>
              <p>You haven't created any newsletter posts yet.</p>
            </div>
          ) : (
            <div style={styles.myPostsList}>
              {(filteredPostId ? myPosts.filter(post => {
                console.log('Filtering My Posts - post:', post.id, 'filteredPostId:', filteredPostId, 'match:', String(post.id) === String(filteredPostId));
                return String(post.id) === String(filteredPostId);
              }) : myPosts).map((post, index) => (
                <div key={post.id} id={`post-${post.id}`} style={styles.myPostCard}>
                  <div style={styles.myPostHeader}>
                    <div style={styles.myPostDate}>
                      {formatDate(post.createddate)}
                    </div>
                    <div style={styles.myPostRecipients}>
                      <button
                        onClick={() => {
                          let assignees = post.assignees;
                          if (typeof assignees === 'string') {
                            try {
                              assignees = JSON.parse(assignees);
                            } catch {
                              assignees = [];
                            }
                          }
                          handleRecipientsClick(post.id, assignees);
                        }}
                        style={styles.recipientsButton}
                        className="subtle-button recipients-button"
                      >
                        Recipients: {post.assignees ? 
                          (() => {
                            let assignees = post.assignees;
                            if (typeof assignees === 'string') {
                              try {
                                assignees = JSON.parse(assignees);
                              } catch {
                                assignees = [];
                              }
                            }
                            return Array.isArray(assignees) ? assignees.length : 0;
                          })() : 0
                        }
                      </button>
                    </div>
                  </div>
                  {getSettingsStatusText(post) && (
                    <div style={styles.settingsStatus}>
                      ⚙️ {getSettingsStatusText(post)}
                    </div>
                  )}
                  
                  {showRecipients === post.id && (
                    <div style={styles.recipientsSection}>
                      <h5 style={styles.recipientsTitle}>Recipients</h5>
                      {loadingRecipients[post.id] ? (
                        <div style={styles.loadingRecipients}>Loading recipients...</div>
                      ) : recipientsList[post.id] && recipientsList[post.id].length > 0 ? (
                        <div style={styles.recipientsList} className="recipientsList">
                          {recipientsList[post.id].map((recipient, recipientIndex) => (
                            <div key={recipient.account_id} style={styles.recipientCard}>
                              <div style={styles.recipientName}>
                                {recipient.first_name && recipient.last_name 
                                  ? `${recipient.first_name} ${recipient.last_name}`
                                  : 'Unknown User'
                                }
                              </div>
                              <div style={styles.recipientEmail}>
                                {recipient.primary_email || 'No email'}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={styles.noRecipients}>No recipients found.</div>
                      )}
                    </div>
                  )}
                  
                  <div style={styles.myPostTitle}>
                    {post.title || 'Untitled Post'}
                  </div>
                  
                  <div style={styles.myPostContent}>
                    <div 
                      dangerouslySetInnerHTML={{ __html: post.content }}
                      style={styles.myPostContentHtml}
                    />
                  </div>
                  
                  <div style={styles.postActions}>
                    {canUserReplyToMainPost(post) && (
                      <button
                        onClick={() => handleReplyClick(post.id)}
                        style={styles.replyButton}
                        className="subtle-button"
                      >
                        💬 Reply
                      </button>
                    )}
                    {canUserViewComments(post) && (
                      <button
                        onClick={() => handleViewComments(post.id)}
                        style={styles.viewCommentsButton}
                        className="subtle-button"
                      >
                        {showComments[post.id] ? '👁️ Hide Comments' : '👁️ View Comments'}
                      </button>
                    )}
                    {shouldShowMyResponseButton(post) && (
                      <button
                        onClick={() => handleViewMyResponse(post.id)}
                        style={styles.myResponseButton}
                        className="subtle-button"
                      >
                        {showMyResponse[post.id] ? '👁️ Hide My Response' : '👁️ My Response'}
                      </button>
                    )}
                    <button
                                              onClick={() => handleEditClick(post.id, post.content, post.title, {
                          allowMainResponse: post.allowmainresponse,
                          allowCommentResponse: post.allowcommentresponse,
                          notifyMeOnResponses: post.notifymeonresponses,
                          allowAssigneesViewResponses: post.allowassigneesviewresponses
                        })}
                      style={styles.editButton}
                      className="subtle-button"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                  
                  {showReplyForm === post.id && (
                    <div style={styles.replyForm} className="replyForm">
                      <div style={styles.quillWrapper}>
                        <ReactQuill
                          value={replyContent}
                          onChange={setReplyContent}
                          modules={quillModules}
                          placeholder="Write your reply..."
                          style={styles.replyQuill}
                        />
                      </div>
                      <div style={styles.replyFormActions} className="replyFormActions">
                        <button
                          onClick={() => handleSubmitReply(post.id)}
                          disabled={submittingReply || !replyContent.trim()}
                          style={styles.submitReplyButton}
                        >
                          {submittingReply ? 'Submitting...' : 'Submit Reply'}
                        </button>
                        <button
                          onClick={handleCancelReply}
                          style={styles.cancelReplyButton}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {showEditForm === post.id && (
                    <div style={styles.editForm} className="editForm">
                      <div style={styles.editTitleSection}>
                        <label style={styles.editLabel}>Post Title *</label>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Enter a title for your newsletter post..."
                          style={styles.editTitleInput}
                          maxLength={200}
                        />
                      </div>
                      
                      <div style={styles.editSettingsSection}>
                        <label style={styles.editLabel}>Post Settings</label>
                        <div style={styles.editSettingsGrid}>
                          <div style={styles.editSettingItem}>
                            <label style={styles.editSettingLabel}>
                              <input
                                type="checkbox"
                                checked={editSettings.allowMainResponse}
                                onChange={(e) => setEditSettings(prev => ({
                                  ...prev,
                                  allowMainResponse: e.target.checked
                                }))}
                                style={styles.editCheckbox}
                              />
                              Allow Main Response
                            </label>
                          </div>
                          <div style={styles.editSettingItem}>
                            <label style={styles.editSettingLabel}>
                              <input
                                type="checkbox"
                                checked={editSettings.allowCommentResponse}
                                onChange={(e) => setEditSettings(prev => ({
                                  ...prev,
                                  allowCommentResponse: e.target.checked
                                }))}
                                style={styles.editCheckbox}
                              />
                              Allow Comment Response
                            </label>
                          </div>
                          <div style={styles.editSettingItem}>
                            <label style={styles.editSettingLabel}>
                              <input
                                type="checkbox"
                                checked={editSettings.notifyMeOnResponses}
                                onChange={(e) => setEditSettings(prev => ({
                                  ...prev,
                                  notifyMeOnResponses: e.target.checked
                                }))}
                                style={styles.editCheckbox}
                              />
                              Notify Me on Responses
                            </label>
                          </div>
                          <div style={styles.editSettingItem}>
                            <label style={styles.editSettingLabel}>
                              <input
                                type="checkbox"
                                checked={editSettings.allowAssigneesViewResponses}
                                onChange={(e) => setEditSettings(prev => ({
                                  ...prev,
                                  allowAssigneesViewResponses: e.target.checked
                                }))}
                                style={styles.editCheckbox}
                              />
                              Allow Recipients View Responses
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div style={styles.quillWrapper}>
                        <label style={styles.editLabel}>Post Content *</label>
                        <ReactQuill
                          value={editContent}
                          onChange={setEditContent}
                          modules={quillModules}
                          placeholder="Edit your post content..."
                          style={styles.editQuill}
                        />
                      </div>
                      <div style={styles.editFormActions} className="editFormActions">
                        <button
                          onClick={() => handleSubmitEdit(post.id)}
                          disabled={submittingEdit || !editContent.trim() || !editTitle.trim()}
                          style={styles.submitEditButton}
                        >
                          {submittingEdit ? 'Updating...' : 'Update Post'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={styles.cancelEditButton}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {showComments[post.id] && (
                    <div style={styles.commentsSection}>
                      <h4 style={styles.commentsTitle}>Comments</h4>
                      {loadingComments[post.id] ? (
                        <div style={styles.loadingComments}>Loading comments...</div>
                      ) : comments[post.id] && comments[post.id].length > 0 ? (
                        <div style={styles.commentsList}>
                          {comments[post.id].map((comment, commentIndex) => renderCommentWithReplies(comment, post))}
                        </div>
                      ) : (
                        <div style={styles.noComments}>No comments yet.</div>
                      )}
                    </div>
                  )}

                  {showMyResponse[post.id] && (
                    <div style={styles.myResponseSection}>
                      <h4 style={styles.myResponseTitle}>My Responses</h4>
                      {loadingComments[post.id] ? (
                        <div style={styles.loadingComments}>Loading your responses...</div>
                      ) : getUserResponses(post).length > 0 ? (
                        <div style={styles.commentsList}>
                          {getUserResponses(post).map((response, responseIndex) => renderCommentWithReplies(response, post))}
                        </div>
                      ) : (
                        <div style={styles.noComments}>You haven't responded to this post yet.</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'toMe' && (
        <div style={styles.postsContainer}>
          {posts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📬</div>
              <h3 style={styles.emptyTitle}>No Newsletter Posts</h3>
              <p style={styles.emptyMessage}>
                You haven't received any newsletter posts yet. When someone creates a post and includes you as a recipient, it will appear here.
              </p>
            </div>
          ) : (
            <>
              {filteredPostId && (
                <div style={styles.filterBanner}>
                  <span>Showing filtered post</span>
                  <button onClick={clearFilter} style={styles.clearFilterButton}>
                    Show All Posts
                  </button>
                </div>
              )}
              {(filteredPostId ? posts.filter(post => {
                console.log('Filtering post:', post.id, 'filteredPostId:', filteredPostId, 'match:', String(post.id) === String(filteredPostId));
                return String(post.id) === String(filteredPostId);
              }) : posts).map((post, index) => (
            <div 
              key={post.id} 
              id={`post-${post.id}`}
              style={styles.postCard}
            >
              <div style={styles.postHeader}>
                <div style={styles.postInfo}>
                  <div style={styles.postTitle}>
                    {post.title || 'Untitled Post'}
                  </div>
                  <div style={styles.postMeta}>
                    <span style={styles.postAuthor}>
                      Posted by {post.first_name && post.last_name 
                        ? `${post.first_name} ${post.last_name}`
                        : 'Unknown Author'
                      }
                    </span>
                    <span style={styles.postDate}>
                      {formatDate(post.createddate)}
                    </span>
                  </div>
                  {getSettingsStatusText(post) && (
                    <div style={styles.settingsStatus}>
                      ⚙️ {getSettingsStatusText(post)}
                    </div>
                  )}
                </div>
                <div style={styles.postType}>
                  <span style={styles.typeBadge}>{post.type}</span>
                </div>
              </div>
              
              <div style={styles.postContent}>
                <div 
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  style={styles.contentHtml}
                />
              </div>
              
              <div style={styles.postActions}>
                {canUserReplyToMainPost(post) && (
                  <button
                    onClick={() => handleReplyClick(post.id)}
                    style={styles.replyButton}
                    className="subtle-button"
                  >
                    💬 Reply
                  </button>
                )}
                {canUserViewComments(post) && (
                  <button
                    onClick={() => handleViewComments(post.id)}
                    style={styles.viewCommentsButton}
                    className="subtle-button"
                  >
                    {showComments[post.id] ? '👁️ Hide Comments' : '👁️ View Comments'}
                  </button>
                )}
                {shouldShowMyResponseButton(post) && (
                  <button
                    onClick={() => handleViewMyResponse(post.id)}
                    style={styles.myResponseButton}
                    className="subtle-button"
                  >
                    {showMyResponse[post.id] ? '👁️ Hide My Response' : '👁️ My Response'}
                  </button>
                )}
              </div>
              
              {showReplyForm === post.id && (
                <div style={styles.replyForm} className="replyForm">
                  <div style={styles.quillWrapper}>
                    <ReactQuill
                      value={replyContent}
                      onChange={setReplyContent}
                      modules={quillModules}
                      placeholder="Write your reply..."
                      style={styles.replyQuill}
                    />
                  </div>
                  <div style={styles.replyFormActions} className="replyFormActions">
                    <button
                      onClick={() => handleSubmitReply(post.id)}
                      disabled={submittingReply || !replyContent.trim()}
                      style={styles.submitReplyButton}
                    >
                      {submittingReply ? 'Submitting...' : 'Submit Reply'}
                    </button>
                    <button
                      onClick={handleCancelReply}
                      style={styles.cancelReplyButton}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {showComments[post.id] && (
                <div style={styles.commentsSection}>
                  <h4 style={styles.commentsTitle}>Comments</h4>
                  {loadingComments[post.id] ? (
                    <div style={styles.loadingComments}>Loading comments...</div>
                  ) : comments[post.id] && comments[post.id].length > 0 ? (
                    <div style={styles.commentsList}>
                      {comments[post.id].map((comment, commentIndex) => renderCommentWithReplies(comment, post))}
                    </div>
                  ) : (
                    <div style={styles.noComments}>No comments yet.</div>
                  )}
                </div>
              )}

              {showMyResponse[post.id] && (
                <div style={styles.myResponseSection}>
                  <h4 style={styles.myResponseTitle}>My Responses</h4>
                  {loadingComments[post.id] ? (
                    <div style={styles.loadingComments}>Loading your responses...</div>
                  ) : getUserResponses(post).length > 0 ? (
                    <div style={styles.commentsList}>
                      {getUserResponses(post).map((response, responseIndex) => renderCommentWithReplies(response, post))}
                    </div>
                  ) : (
                    <div style={styles.noComments}>You haven't responded to this post yet.</div>
                  )}
                </div>
              )}
              
              {index < posts.length - 1 && <hr style={styles.divider} />}
            </div>
          ))}
             </>
           )}
         </div>
       )}

      {activeTab === 'repliedTo' && (
        <div style={styles.postsContainer}>
          {loadingRepliedTo ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>⏳</div>
              <h3 style={styles.emptyTitle}>Loading...</h3>
              <p style={styles.emptyMessage}>
                Loading posts you've replied to...
              </p>
            </div>
          ) : repliedToPosts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>💬</div>
              <h3 style={styles.emptyTitle}>No Replies Yet</h3>
              <p style={styles.emptyMessage}>
                You haven't replied to any newsletter posts yet. When you reply to a post, it will appear here.
              </p>
            </div>
          ) : (
            <>
              {filteredPostId && (
                <div style={styles.filterBanner}>
                  <span>Showing filtered post</span>
                  <button onClick={clearFilter} style={styles.clearFilterButton}>
                    Show All Posts
                  </button>
                </div>
              )}
              {(filteredPostId ? repliedToPosts.filter(post => {
            console.log('Filtering post:', post.id, 'filteredPostId:', filteredPostId, 'match:', String(post.id) === String(filteredPostId));
            return String(post.id) === String(filteredPostId);
          }) : repliedToPosts).map((post, index) => (
            <div 
              key={post.id} 
              id={`post-${post.id}`}
              style={styles.postCard}
            >
              <div style={styles.postHeader}>
                <div style={styles.postInfo}>
                  <div style={styles.postTitle}>
                    {post.title || 'Untitled Post'}
                  </div>
                  <div style={styles.postMeta}>
                    <span style={styles.postAuthor}>
                      Posted by {post.first_name && post.last_name 
                        ? `${post.first_name} ${post.last_name}`
                        : 'Unknown Author'
                      }
                    </span>
                    <span style={styles.postDate}>
                      {formatDate(post.createddate)}
                    </span>
                  </div>
                  {getSettingsStatusText(post) && (
                    <div style={styles.settingsStatus}>
                      ⚙️ {getSettingsStatusText(post)}
                    </div>
                  )}
                </div>
                <div style={styles.postType}>
                  <span style={styles.typeBadge}>{post.type}</span>
                </div>
              </div>
              
              <div style={styles.postContent}>
                <div 
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  style={styles.contentHtml}
                />
              </div>
              
              <div style={styles.postActions}>
                {canUserReplyToMainPost(post) && (
                  <button
                    onClick={() => handleReplyClick(post.id)}
                    style={styles.replyButton}
                    className="subtle-button"
                  >
                    💬 Reply
                  </button>
                )}
                {canUserViewComments(post) && (
                  <button
                    onClick={() => handleViewComments(post.id)}
                    style={styles.viewCommentsButton}
                    className="subtle-button"
                  >
                    {showComments[post.id] ? '👁️ Hide Comments' : '👁️ View Comments'}
                  </button>
                )}
                {shouldShowMyResponseButton(post) && (
                  <button
                    onClick={() => handleViewMyResponse(post.id)}
                    style={styles.myResponseButton}
                    className="subtle-button"
                  >
                    {showMyResponse[post.id] ? '👁️ Hide My Response' : '👁️ My Response'}
                  </button>
                )}
              </div>
              
              {showReplyForm === post.id && (
                <div style={styles.replyForm} className="replyForm">
                  <div style={styles.quillWrapper}>
                    <ReactQuill
                      value={replyContent}
                      onChange={setReplyContent}
                      modules={quillModules}
                      placeholder="Write your reply..."
                      style={styles.replyQuill}
                    />
                  </div>
                  <div style={styles.replyFormActions} className="replyFormActions">
                    <button
                      onClick={() => handleSubmitReply(post.id)}
                      disabled={submittingReply || !replyContent.trim()}
                      style={styles.submitReplyButton}
                    >
                      {submittingReply ? 'Submitting...' : 'Submit Reply'}
                    </button>
                    <button
                      onClick={handleCancelReply}
                      style={styles.cancelReplyButton}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {showComments[post.id] && (
                <div style={styles.commentsSection}>
                  <h4 style={styles.commentsTitle}>Comments</h4>
                  {loadingComments[post.id] ? (
                    <div style={styles.loadingComments}>Loading comments...</div>
                  ) : comments[post.id] && comments[post.id].length > 0 ? (
                    <div style={styles.commentsList}>
                      {comments[post.id].map((comment, commentIndex) => renderCommentWithReplies(comment, post))}
                    </div>
                  ) : (
                    <div style={styles.noComments}>No comments yet.</div>
                  )}
                </div>
              )}
              
              {showMyResponse[post.id] && (
                <div style={styles.myResponseSection}>
                  <h4 style={styles.myResponseTitle}>My Responses</h4>
                  {loadingComments[post.id] ? (
                    <div style={styles.loadingComments}>Loading your responses...</div>
                  ) : getUserResponses(post).length > 0 ? (
                    <div style={styles.commentsList}>
                      {getUserResponses(post).map((response, responseIndex) => renderCommentWithReplies(response, post))}
                    </div>
                  ) : (
                    <div style={styles.noComments}>You haven't responded to this post yet.</div>
                  )}
                </div>
              )}
              
              {index < repliedToPosts.length - 1 && <hr style={styles.divider} />}
            </div>
          ))}
             </>
           )}
         </div>
       )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    minHeight: 'calc(100vh - 64px)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem'
  },
  headerActions: {
    marginTop: '1rem'
  },
  createButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#2563eb',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '1rem',
    transition: 'background-color 0.2s'
  },
  myPostsButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#8b5cf6',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '1rem',
    transition: 'background-color 0.2s',
    border: 'none',
    cursor: 'pointer',
    marginLeft: '1rem'
  },
  title: {
    color: '#1D4E89',
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    fontWeight: '700'
  },
  subtitle: {
    color: '#666',
    fontSize: '1.1rem',
    margin: 0
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },
  emptyIcon: {
    fontSize: '4rem',
    marginBottom: '1rem'
  },
  emptyTitle: {
    color: '#374151',
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
    fontWeight: '600'
  },
  emptyMessage: {
    color: '#6b7280',
    fontSize: '1rem',
    maxWidth: '500px',
    margin: '0 auto'
  },
  postsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem'
  },
  postCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb'
  },
  postHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #f3f4f6'
  },
  postInfo: {
    flex: 1
  },
  postTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.25rem',
    lineHeight: '1.3'
  },
  postMeta: {
    fontSize: '0.75rem',
    color: '#6b7280',
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    marginBottom: '0.25rem'
  },
  postAuthor: {
    fontWeight: '500'
  },
  postDate: {
    fontSize: '0.75rem',
    color: '#6b7280'
  },
  postType: {
    marginLeft: '1rem'
  },
  typeBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  postContent: {
    lineHeight: '1.6'
  },
  contentHtml: {
    fontSize: '1rem',
    color: '#374151'
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e5e7eb',
    margin: '2rem 0 0 0'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },
  errorTitle: {
    color: '#dc2626',
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
    fontWeight: '600'
  },
  errorMessage: {
    color: '#6b7280',
    fontSize: '1rem'
  },
  filterBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #fde68a'
  },
  clearFilterButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  myPostsContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem'
  },
  myPostsTitle: {
    color: '#1D4E89',
    fontSize: '1.8rem',
    marginBottom: '1.5rem',
    fontWeight: '600'
  },
  emptyMyPosts: {
    textAlign: 'center',
    padding: '2rem',
    color: '#6b7280',
    fontSize: '1rem'
  },
  myPostsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  myPostCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.5rem',
    backgroundColor: '#f9fafb'
  },
  myPostHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e5e7eb'
  },
  myPostDate: {
    fontSize: '0.875rem',
    color: '#6b7280',
    fontWeight: '500'
  },
  myPostRecipients: {
    fontSize: '0.875rem',
    color: '#059669',
    fontWeight: '500',
    backgroundColor: '#d1fae5',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px'
  },
  myPostTitle: {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '1rem',
    lineHeight: '1.3'
  },
  myPostContent: {
    lineHeight: '1.6'
  },
  myPostContentHtml: {
    fontSize: '1rem',
    color: '#374151'
  },
  repliedPostsButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '1rem',
    transition: 'background-color 0.2s',
    border: 'none',
    cursor: 'pointer',
    marginLeft: '1rem'
  },
  postActions: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f3f4f6'
  },
  replyButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '400',
    transition: 'all 0.2s',
    textDecoration: 'none'
  },
  replyForm: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    position: 'relative',
    overflow: 'hidden'
  },
  replyQuill: {
    width: '100%',
    height: '150px', // Adjust height as needed
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    color: '#374151',
    lineHeight: '1.5'
  },
  replyTextarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px'
  },
  replyFormActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
    position: 'relative',
    zIndex: 2
  },
  submitReplyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  cancelReplyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  viewCommentsButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '400',
    transition: 'all 0.2s',
    marginLeft: '0.5rem'
  },
  commentsSection: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  commentsTitle: {
    color: '#374151',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    fontWeight: '600'
  },
  loadingComments: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
    padding: '1rem'
  },
  commentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  commentCard: {
    backgroundColor: 'white',
    borderRadius: '6px',
    padding: '1rem',
    border: '1px solid #e5e7eb'
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.75rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #f3f4f6'
  },
  commentAuthor: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151'
  },
  commentDate: {
    fontSize: '0.75rem',
    color: '#6b7280'
  },
  commentContent: {
    lineHeight: '1.5',
    marginBottom: '0.5rem'
  },
  commentContentHtml: {
    fontSize: '0.875rem',
    color: '#374151'
  },
  commentActions: {
    marginTop: '0.75rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #f3f4f6'
  },
  replyButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '400',
    transition: 'all 0.2s'
  },
  replyForm: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    position: 'relative',
    overflow: 'hidden'
  },
  quillWrapper: {
    marginBottom: '1rem',
    position: 'relative',
    zIndex: 1
  },
  replyQuill: {
    height: '150px'
  },
  replyFormActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    position: 'relative',
    zIndex: 3
  },
  submitReplyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  nestedReplies: {
    marginTop: '1rem',
    paddingLeft: '1rem',
    borderLeft: '2px solid #e5e7eb'
  },
  noComments: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
    padding: '1rem',
    fontStyle: 'italic'
  },
  editButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '400',
    transition: 'all 0.2s',
    marginLeft: '0.5rem'
  },
  editForm: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    position: 'relative'
  },
  editTitleSection: {
    marginBottom: '1rem'
  },
  editTitleInput: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.9rem',
    outline: 'none',
    fontFamily: 'inherit'
  },
  editSettingsSection: {
    marginBottom: '1rem'
  },
  editSettingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.5rem'
  },
  editSettingItem: {
    display: 'flex',
    alignItems: 'center'
  },
  editSettingLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.8rem',
    color: '#374151',
    cursor: 'pointer'
  },
  editCheckbox: {
    marginRight: '0.5rem',
    width: '14px',
    height: '14px'
  },
  editLabel: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#374151',
    marginBottom: '0.5rem'
  },
  editQuill: {
    width: '100%',
    height: '200px', // Slightly larger for editing
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    color: '#374151',
    lineHeight: '1.5'
  },
  editTextarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '150px'
  },
  editFormActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
    position: 'relative',
    zIndex: 2
  },
  submitEditButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  cancelEditButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  quillWrapper: {
    position: 'relative',
    zIndex: 1
  },
  recipientsButton: {
    padding: '0.25rem 0.75rem',
    backgroundColor: '#d1fae5',
    color: '#059669',
    border: '1px solid #d1fae5',
    borderRadius: '12px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  recipientsSection: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #f3f4f6',
    maxHeight: '200px',
    overflow: 'hidden'
  },
  recipientsTitle: {
    color: '#1D4E89',
    fontSize: '1.1rem',
    marginBottom: '0.75rem',
    fontWeight: '600'
  },
  loadingRecipients: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
    padding: '1rem'
  },
  recipientsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    maxHeight: '150px',
    overflowY: 'auto',
    paddingRight: '0.5rem'
  },
  recipientCard: {
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    border: '1px solid #e5e7eb'
  },
  recipientName: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151'
  },
  recipientEmail: {
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '0.25rem'
  },
  noRecipients: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
    padding: '1rem',
    fontStyle: 'italic'
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  tabSection: {
    display: 'flex',
    gap: '1rem'
  },
  tabButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  activeTabButton: {
    backgroundColor: '#2563eb',
    color: 'white',
    borderColor: '#2563eb'
  },
  createButtonSection: {
    display: 'flex',
    gap: '1rem'
  },
  settingsStatus: {
    fontSize: '0.75rem',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    marginTop: '0.5rem'
  },
  myResponseButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '400',
    transition: 'all 0.2s',
    marginLeft: '0.5rem'
  },
  myResponseSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f3f4f6'
  },
  myResponseTitle: {
    color: '#1D4E89',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    fontWeight: '600'
  }
};

// Add CSS for Quill editor styling
const quillStyles = `
  .ql-editor {
    min-height: 120px;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #374151;
  }
  
  .ql-toolbar {
    border-top: 1px solid #d1d5db;
    border-left: 1px solid #d1d5db;
    border-right: 1px solid #d1d5db;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    background-color: #f9fafb;
  }
  
  .ql-container {
    border-bottom: 1px solid #d1d5db;
    border-left: 1px solid #d1d5db;
    border-right: 1px solid #d1d5db;
    border-top: none;
    border-radius: 0 0 6px 6px;
    background-color: white;
    height: 150px;
    overflow-y: auto;
  }
  
  .ql-editor.ql-blank::before {
    color: #9ca3af;
    font-style: italic;
  }
  
  /* Fix for Quill editor container positioning */
  .quill {
    margin-bottom: 1rem;
  }
  
  /* Ensure the editor doesn't overflow its container */
  .replyForm .quill,
  .editForm .quill {
    position: relative;
    z-index: 1;
  }
  
  /* Add spacing between editor and buttons */
  .replyFormActions,
  .editFormActions {
    margin-top: 1rem;
    position: relative;
    z-index: 2;
  }
  
  /* Additional containment for reply forms */
  .replyForm {
    position: relative;
    overflow: hidden;
  }
  
  .replyForm .quill {
    position: relative;
    z-index: 1;
    max-height: 200px;
    overflow: hidden;
  }
  
  .replyForm .ql-container {
    max-height: 150px;
    overflow-y: auto;
  }
  
  /* Specific fixes for nested comment replies */
  .nestedReplies .replyForm {
    position: relative;
    overflow: hidden;
    z-index: 1;
  }
  
  .nestedReplies .replyForm .quill {
    position: relative;
    z-index: 1;
    max-height: 150px;
    overflow: hidden;
  }
  
  .nestedReplies .replyForm .ql-container {
    max-height: 120px;
    overflow-y: auto;
  }
  
  .nestedReplies .replyForm .replyFormActions {
    position: relative;
    z-index: 3;
    margin-top: 1rem;
  }
  
  /* Ensure Quill toolbar doesn't overlap buttons */
  .replyForm .ql-toolbar {
    position: relative;
    z-index: 1;
  }
  
  .nestedReplies .replyForm .ql-toolbar {
    position: relative;
    z-index: 1;
  }
  
  /* Prevent any absolute positioning from breaking layout */
  .replyForm .ql-editor {
    position: relative;
    z-index: 1;
  }
  
  /* Subtle button hover effects */
  .subtle-button {
    transition: all 0.2s ease;
  }
  
  .subtle-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #374151 !important;
  }
  
  /* Recipients button hover effect */
  .recipients-button:hover {
    background-color: #a7f3d0 !important;
    border-color: #10b981 !important;
    color: #047857 !important;
  }
  
  /* Custom scrollbar for recipients list */
  .recipientsList::-webkit-scrollbar {
    width: 6px;
  }
  
  .recipientsList::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  .recipientsList::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  .recipientsList::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  /* Tab button hover effects */
  .tab-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #374151 !important;
  }
  
  .tab-button.active:hover {
    background-color: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
    color: white !important;
  }
`;

export default NewsletterViewer; 